/**
 * ReadyWorklist — ADR-122 D2/D6/D8b: the changes waiting to go live, named.
 *
 * WHAT THIS REPLACES, AND WHY
 *
 * The ADR-114 D2 banner answered a real question — "is something waiting to go
 * live?" — with the wrong object: a repo-wide commit count, rendered inside one
 * agent's tab, mixing operator changes with automated platform syncs and merge
 * commits. An owner reading "2 changes waiting to go live — fleet-wide, not just
 * this agent" cannot tell WHAT is waiting, who proposed it, or whether it is
 * even theirs. D8b replaces that count with named Changes and one aggregate
 * Platform update, all taken live with one shared action.
 *
 * TWO VERBS, ON PURPOSE
 *
 * Approving a change on its review page ships it in one click (ADR-122 amendment
 * 2026-11-03), so what waits here is what reached develop without that: platform
 * syncs, agents' auto-merged self-edits, changes merged outside Mission Control, an
 * approve whose promote failed. Going live stays a person's choice. One Apply promotes
 * everything on this list together: N changes, one restart. Hence the single
 * Apply below the list rather than a button per row — a per-row Apply would
 * quietly reintroduce exactly the restart-per-change cost the batching is for.
 *
 * WHAT IS DELIBERATELY NOT HERE
 *
 * - Platform syncs are a separate category, shown as one aggregate Platform
 *   update row and never attributed to an operator or agent.
 * - `pull request`, `merge`, `develop`, `main` do not appear in operator-facing
 *   copy. A fleet owner should never need to know those words. The details a
 *   technician or agent needs live in the drill-down (D7), not here.
 * - A degraded read SAYS SO instead of rendering a shorter list as if it were
 *   complete. Silence about a change is a defect (D5) — that is the whole reason
 *   this surface exists.
 *
 * THE BANNER TELLS THE TRUTH, OR SAYS NOTHING (ADR-130 D5)
 *
 * This component used to hold a local `applied` boolean: set once when Apply
 * returned, never reset, no polling, no reconciliation. It could not be wrong,
 * because it was not a claim about the world — it was a claim about a click.
 *
 * On 2026-08-06 that produced the defect worth remembering. Two fleets could not
 * take ANY change live for 18 and 23 hours, and this surface rendered
 * "Taking these live now." in green, directly above the unchanged list of the
 * changes that had not shipped. Two contradictory claims on one screen, and the
 * reassuring one was wrong. The API even returned `queued: true` for exactly
 * this case; nothing read it.
 *
 * So the banner renders `phase` from GET /api/promotion/status, which the
 * ADR-130 reconciler resolves from GitHub. It remains on Updates until the
 * promotion resolves, including after the pending diff is empty: an empty diff
 * is not Live (ADR-122 D3).
 *
 * The operator is never asked to fix a stall. The platform retries; if it runs
 * out of retries it says so plainly and says it has told the platform team.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ForbiddenError, UnauthorizedError } from '../api/client'
import type { PendingChangesStatus, PendingChangeItem, PromotionStatus } from '../api/client'
import { notifyPendingChangesChanged, PENDING_CHANGES_EVENT } from '../hooks/usePendingChangeSignal'

/** How often to re-read promotion state while one is in flight. */
const PROMOTION_POLL_MS = 10_000

/** Extracts {status, error, message} from the api client's "HTTP <status>: <body>"
 * errors — same convention as Updates/FleetHealth/ImprovementPRReview.
 *
 * `error` is the machine-readable code, needed to tell one 409 from another now
 * that the promote gate distinguishes a genuinely red fleet from a stall. */
function parseApiError(err: unknown, fallback: string): { status: number | null; error: string | null; message: string } {
  if (!(err instanceof Error)) return { status: null, error: null, message: fallback }
  const m = /^HTTP (\d+): ([\s\S]*)$/.exec(err.message)
  if (!m) return { status: null, error: null, message: err.message || fallback }
  try {
    const body = JSON.parse(m[2]) as { error?: string; detail?: string }
    return { status: Number(m[1]), error: body.error ?? null, message: body.detail || body.error || fallback }
  } catch {
    return { status: Number(m[1]), error: null, message: fallback }
  }
}

/**
 * Who proposed this change, in words.
 *
 * D4: the GitHub author is an implementation detail of the execution path and is
 * never displayed as the author — every self-improvement is committed by the
 * same automation identity, so showing it would make two different agents'
 * changes indistinguishable. Prefer the owning agent from the artifacts; fall
 * back to a plain statement rather than inventing an actor.
 */
export function provenanceLabel(ch: PendingChangeItem): string {
  const agent = ch.artifacts.find(a => a.agent)?.agent
  if (agent) return `proposed by ${agent}`
  if (ch.origin === 'direct_commit') return 'changed directly in this fleet'
  return 'proposed in this fleet'
}

/** A one-line "what else this touched", when a Change spans more than its title. */
export function secondaryLabel(ch: PendingChangeItem): string {
  const extra = ch.artifacts.length - 1
  if (extra <= 0) return ''
  return `and ${extra} more ${extra === 1 ? 'item' : 'items'}`
}

/** The worklist heading. n = operator Changes; hasPlatformUpdate = platform_syncs non-empty. */
export function readyHeading(n: number, hasPlatformUpdate: boolean): string {
  if (hasPlatformUpdate) {
    if (n === 0) return 'Platform update waiting to go live'
    return `${n} ${n === 1 ? 'change' : 'changes'} and a platform update waiting to go live`
  }
  return `${n} ${n === 1 ? 'change' : 'changes'} waiting to go live`
}

const PLATFORM_COST_NOTE = "Going live may briefly pause this fleet's agents. If a restart is needed, the platform waits for active work to finish and defers if the fleet stays busy."
const OPERATOR_COST_NOTE = "Going live restarts this fleet's agents once."

/** Confirmation for the fleet-wide Take live action. */
export function applyConfirmation(n: number, hasPlatformUpdate: boolean): string {
  if (hasPlatformUpdate) {
    const question = n === 0
      ? 'Take the platform update live now?'
      : `Take ${n === 1 ? 'this change' : `these ${n} changes`} and the platform update live now?`
    return `${question} ${PLATFORM_COST_NOTE}`
  }
  const question = `Take ${n === 1 ? 'this change' : `these ${n} changes`} live now?`
  return `${question} ${OPERATOR_COST_NOTE}`
}

/** Confirmation for one-click approve, which ships (ADR-122 amendment 2026-11-03). */
export function approveConfirmation(): string {
  return `Approve this change and take it live now? Anything else waiting to go live goes with it. ${PLATFORM_COST_NOTE}`
}

/** True while a promotion has not resolved. */
export function promotionInProgress(p: PromotionStatus | null): boolean {
  return Boolean(p && (p.in_flight || p.phase === 'awaiting_checks' || p.phase === 'merging' || p.phase === 'deploying'))
}

function ChangeRow({ ch }: { ch: PendingChangeItem }) {
  const secondary = secondaryLabel(ch)
  return (
    <li className="px-4 py-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{ch.title}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {provenanceLabel(ch)}
            {secondary && <span className="text-gray-400"> · {secondary}</span>}
          </p>
        </div>
      </div>
    </li>
  )
}

function PlatformUpdateRow({ syncs }: { syncs: PendingChangeItem[] }) {
  return (
    <li className="px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">Platform update</p>
        <p className="text-xs text-gray-500 mt-0.5">Platform fixes are ready for this fleet.</p>
        <details className="mt-1 text-xs text-gray-500">
          <summary className="cursor-pointer">Details</summary>
          <ul className="mt-1 list-disc pl-5">
            {syncs.map((sync, index) => (
              <li key={`${sync.title}-${sync.pr_number ?? index}`}>
                {sync.title}{sync.pr_number !== undefined ? ` · #${sync.pr_number}` : ''}
              </li>
            ))}
          </ul>
        </details>
      </div>
    </li>
  )
}

export function ReadyWorklist() {
  const [status, setStatus] = useState<PendingChangesStatus | null>(null)
  const [loaded, setLoaded] = useState(false)
  // A failed READ is its own state, distinct from "nothing is waiting". Falling
  // back to an empty list would report "all clear" on an outage — the exact
  // substitution D5 calls a defect.
  const [readError, setReadError] = useState<string | null>(null)

  const [applying, setApplying] = useState(false)
  const [applyError, setApplyError] = useState<string | null>(null)
  const [verifying, setVerifying] = useState(false)
  const [promotionReadFailed, setPromotionReadFailed] = useState(false)
  // ADR-130 D5: the promotion's RESOLVED phase, not a memory of having clicked.
  const [promotion, setPromotion] = useState<PromotionStatus | null>(null)
  const pendingRequest = useRef(0)
  const promotionRequest = useRef(0)
  const lastSeenPhase = useRef<PromotionStatus['phase'] | null>(null)

  const load = useCallback(() => {
    const request = ++pendingRequest.current
    api.getPendingChanges()
      .then(s => {
        if (request !== pendingRequest.current) return
        setStatus(s)
        setReadError(null)
      })
      .catch(err => {
        if (request !== pendingRequest.current) return
        if (err instanceof UnauthorizedError) throw err
        const { message } = parseApiError(err, 'Could not read what is waiting to go live.')
        setReadError(message)
        setStatus(null)
      })
      .finally(() => { if (request === pendingRequest.current) setLoaded(true) })
  }, [])

  useEffect(load, [load])

  const loadPromotion = useCallback(() => {
    const request = ++promotionRequest.current
    return api.getPromotionStatus()
      .then(p => {
        if (request === promotionRequest.current) {
          setPromotion(p)
          lastSeenPhase.current = p.phase
          setVerifying(false)
          setPromotionReadFailed(false)
        }
        return request === promotionRequest.current ? p : null
      })
      // A failed status read must not blank the banner: the promotion is still
      // running, we just cannot describe it. Keep the last known phase.
      .catch(() => {
        if (request === promotionRequest.current) setPromotionReadFailed(true)
        return null
      })
  }, [])

  useEffect(() => {
    void loadPromotion()
  }, [loadPromotion])

  // Poll while a promotion is in flight or an accepted apply still needs a
  // successful status read. The list is re-read through the notification.
  const inFlight = promotion?.in_flight ?? false
  const polling = inFlight || verifying
  const pollRef = useRef<number | null>(null)
  useEffect(() => {
    if (!polling) return
    pollRef.current = window.setInterval(() => {
      const previousPhase = lastSeenPhase.current
      void loadPromotion().then(p => {
        if (p && (p.phase !== previousPhase || !p.in_flight)) notifyPendingChangesChanged()
      })
    }, PROMOTION_POLL_MS)
    return () => {
      if (pollRef.current !== null) window.clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [polling, loadPromotion])

  useEffect(() => {
    const refresh = () => {
      load()
      void loadPromotion()
    }
    window.addEventListener('focus', refresh)
    window.addEventListener(PENDING_CHANGES_EVENT, refresh)
    return () => {
      window.removeEventListener('focus', refresh)
      window.removeEventListener(PENDING_CHANGES_EVENT, refresh)
    }
  }, [load, loadPromotion])

  const handleApply = useCallback(async () => {
    const n = status?.changes.length ?? 0
    const hasPlatformUpdate = (status?.platform_syncs?.length ?? 0) > 0
    if (!window.confirm(applyConfirmation(n, hasPlatformUpdate))) {
      return
    }
    setApplying(true)
    setApplyError(null)
    try {
      await api.deployWorkflowsPending()
      setVerifying(true)
      notifyPendingChangesChanged()
    } catch (err) {
      if (err instanceof UnauthorizedError) throw err
      if (err instanceof ForbiddenError) {
        setApplyError('Permission denied — fleet owner required.')
      } else {
        const { status: code, message, error } = parseApiError(err, 'Could not take the changes live — try again.')
        // The only 409 left is a genuinely red fleet (ADR-130). The old copy
        // here — "still being checked" — was said to athena for 23 hours while
        // nothing was checking anything, so it is deliberately gone.
        setApplyError(code === 409 && error === 'develop_red'
          ? "This can't go live yet: the fleet's automated checks are failing."
          : message)
      }
    } finally {
      setApplying(false)
    }
  }, [status])

  if (!loaded) return null

  if (readError) {
    return (
      <section className="bg-white rounded-lg border border-orange-200 p-4">
        <h2 className="text-sm font-semibold text-gray-900">Changes waiting to go live</h2>
        <p className="text-sm text-orange-800 mt-1">
          {readError} There may be changes waiting — this is not a clear signal.
        </p>
      </section>
    )
  }

  // No GitHub integration: say nothing rather than render a false "all clear".
  if (!status || !status.configured) return null

  const changes = status.changes ?? []
  const syncs = status.platform_syncs ?? []
  const hasSync = syncs.length > 0
  const hasReady = changes.length > 0 || hasSync
  const degradedLine = status.degraded && (
    <p className="text-xs text-orange-800 mt-1">
      Some detail could not be read, so this list may be incomplete.
    </p>
  )
  const unverifiedLine = verifying && promotionReadFailed && (
    <p className="text-xs text-amber-700 mt-1">We couldn't read the progress just now. Checking again.</p>
  )

  if (!hasReady) {
    const inProgress = applying || verifying || promotionInProgress(promotion)
    const failed = !inProgress && promotion?.phase === 'failed'
    const border = inProgress ? 'border-amber-200' : failed ? 'border-red-200' : 'border-gray-200'
    const heading = inProgress ? 'Taking updates live' : 'Changes waiting to go live'
    return (
      <section className={`bg-white rounded-lg border ${border} p-4`}>
        <h2 className="text-sm font-semibold text-gray-900">{heading}</h2>
        {inProgress || failed ? (
          <>
            <PromotionBanner promotion={promotion} retrying={applying} />
            {unverifiedLine}
          </>
        ) : (
          <p className="text-sm text-gray-600 mt-1">
            Nothing waiting — everything proposed for this fleet is live.
          </p>
        )}
        {degradedLine}
      </section>
    )
  }

  return (
    <section className="bg-white rounded-lg border border-gray-200">
      <div className="px-4 pt-4">
        <h2 className="text-sm font-semibold text-gray-900">{readyHeading(changes.length, hasSync)}</h2>
        {degradedLine}
      </div>

      <ul className="mt-2 divide-y divide-gray-100">
        {changes.map((ch, i) => <ChangeRow key={`${ch.title}-${i}`} ch={ch} />)}
        {hasSync && <PlatformUpdateRow syncs={syncs} />}
      </ul>

      <div className="px-4 py-3 border-t border-gray-100 space-y-2">
        {!applyError && <PromotionBanner promotion={promotion} onRetry={handleApply} retrying={applying} />}
        {applyError && <p className="text-sm text-red-700">{applyError}</p>}
        {unverifiedLine}
        <p className="text-xs text-gray-500">{hasSync ? PLATFORM_COST_NOTE : OPERATOR_COST_NOTE}</p>
        {hasSync && changes.length > 0 && (
          <p className="text-xs text-gray-500">Take live applies these changes and the platform update together.</p>
        )}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleApply}
            disabled={applying}
            className="shrink-0 px-3 py-1.5 text-sm font-medium rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {applying ? 'Taking live…' : 'Take live'}
          </button>
        </div>
      </div>
    </section>
  )
}

/**
 * The in-flight promotion, in one line (ADR-130 D5).
 *
 * Rendered with ready work and while an empty pending list still has a
 * promotion in progress. Deliberately never green: the promotion has not
 * resolved, so nothing is claimed as live.
 *
 * `failed` is the only case that asks anything of the operator, and what it asks
 * is "press this again if you like" — not "go fix GitHub". The retries already
 * happened, unattended, and the platform team has already been told.
 */
export function PromotionBanner({
  promotion, onRetry, retrying,
}: {
  promotion: PromotionStatus | null
  onRetry?: () => void
  retrying: boolean
}) {
  if (!promotion || promotion.phase === 'idle' || promotion.phase === 'live') return null

  if (promotion.phase === 'failed') {
    return (
      <div className="text-sm text-red-700 space-y-1">
        <p>
          <span>{promotion.reason || "This couldn't go live."}</span>
          {promotion.attempts > 0 && (
            <> We retried {promotion.attempts === 1 ? 'once' : `${promotion.attempts} times`} automatically.</>
          )}
        </p>
        <p className="text-xs text-red-600">The platform team has been notified.</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            className="text-xs font-medium underline underline-offset-2 disabled:opacity-50"
          >
            Try again
          </button>
        )}
      </div>
    )
  }

  // awaiting_checks | merging | deploying — all still in progress. Amber, not
  // green: nothing has shipped while this list is still populated.
  return (
    <p className="text-sm text-amber-800">
      <span>{promotion.reason || 'Taking these live.'}</span>
      {promotion.attempts > 0 && (
        <span className="text-xs text-amber-700"> The platform is clearing a hold-up for you.</span>
      )}
      {promotion.degraded && (
        <span className="text-xs text-amber-700"> Some detail could not be read just now.</span>
      )}
    </p>
  )
}
